import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../services/api';

const NotificationContext = createContext();

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  // Function to fetch server notifications
  const fetchServerNotifications = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await api.get('/profile/notifications');
      const data = response.data;
        
        // Convert server notifications to client format and add them
        if (data.notifications && data.notifications.length > 0) {
          data.notifications.forEach(serverNotif => {
            // Check if we already have this notification
            const exists = notifications.some(n => 
              (n.type === 'scanner_attack' && 
               n.attackerUsername === serverNotif.attackerUsername &&
               new Date(n.timestamp).getTime() === new Date(serverNotif.timestamp).getTime()) ||
              (n.type === serverNotif.type && 
               new Date(n.timestamp).getTime() === new Date(serverNotif.timestamp).getTime())
            );

            if (!exists) {
              let notificationData = {};

              // Handle different notification types
              switch (serverNotif.type) {
                case 'scanner_attack':
                  notificationData = {
                    type: 'scanner_attack',
                    category: 'defense',
                    title: serverNotif.title || '💸 Savings Account Hacked!',
                    message: serverNotif.message,
                    details: serverNotif.details,
                    icon: '💸',
                    color: '#FF5722',
                    attackerUsername: serverNotif.attackerUsername,
                    attackerId: serverNotif.attackerId,
                    amountStolen: serverNotif.amountStolen
                  };
                  break;

                case 'swat_detection':
                  notificationData = {
                    type: 'swat_detection',
                    category: 'urgent',
                    title: '🚔 SWAT DETECTED!',
                    message: serverNotif.message,
                    details: serverNotif.details,
                    icon: '🚔',
                    color: '#FF0000',
                    escapeDeadline: serverNotif.escapeDeadline,
                    attackType: serverNotif.attackType
                  };
                  break;

                case 'swat_escape':
                  notificationData = {
                    type: 'swat_escape',
                    category: 'success',
                    title: '✅ Escaped SWAT!',
                    message: serverNotif.message,
                    details: serverNotif.details,
                    icon: '✅',
                    color: '#4CAF50'
                  };
                  break;

                case 'jail_sentence':
                  notificationData = {
                    type: 'jail_sentence',
                    category: 'urgent',
                    title: '🔒 Sent to Jail!',
                    message: serverNotif.message,
                    details: serverNotif.details,
                    icon: '🔒',
                    color: '#FF0000',
                    jailDuration: serverNotif.jailDuration
                  };
                  break;

                default:
                  notificationData = {
                    type: serverNotif.type,
                    category: 'general',
                    title: serverNotif.title,
                    message: serverNotif.message,
                    details: serverNotif.details,
                    icon: '📢',
                    color: '#2196F3'
                  };
              }

              // Add common properties
              notificationData.timestamp = serverNotif.timestamp;
              notificationData.serverId = serverNotif._id;

              addNotification(notificationData);
            }
          });

          // Mark server notifications as processed
          await api.delete('/profile/notifications');
        }
    } catch (error) {
      console.error('Error fetching server notifications:', error);
    }
  };

  // Load notifications from localStorage on mount and fetch server notifications
  useEffect(() => {
    const savedNotifications = localStorage.getItem('whiteRabbit_notifications');
    if (savedNotifications) {
      try {
        const parsed = JSON.parse(savedNotifications);
        setNotifications(parsed);
        setUnreadCount(parsed.filter(n => !n.read).length);
      } catch (error) {
        console.error('Error loading notifications:', error);
      }
    }

    // Fetch server notifications on mount
    fetchServerNotifications();

    // Poll for server notifications every 30 seconds
    const interval = setInterval(fetchServerNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  // Save notifications to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('whiteRabbit_notifications', JSON.stringify(notifications));
    setUnreadCount(notifications.filter(n => !n.read).length);
  }, [notifications]);

  const addNotification = (notification) => {
    const newNotification = {
      id: Date.now() + Math.random(),
      timestamp: new Date().toISOString(),
      read: false,
      ...notification
    };

    // Check for duplicate notifications based on type and key details
    setNotifications(prev => {
      // For incoming attacks, only allow one notification per attacker
      if (notification.type === 'incoming_attack') {
        const attackerUsername = notification.attackerUsername;
        const existingAttack = prev.find(n => 
          n.type === 'incoming_attack' && 
          n.attackerUsername === attackerUsername &&
          !n.read // Only check unread notifications
        );
        
        if (existingAttack) {
          // Don't add duplicate incoming attack notification for the same attacker
          console.log(`Preventing duplicate incoming attack notification from ${attackerUsername}`);
          return prev;
        }
      }
      
      // For attack completions, prevent duplicates based on target and timestamp (within 1 minute)
      if (notification.type === 'attack_completed') {
        const now = new Date();
        const oneMinuteAgo = new Date(now.getTime() - 60000);
        
        const recentDuplicate = prev.find(n => 
          n.type === 'attack_completed' && 
          n.message === notification.message &&
          new Date(n.timestamp) > oneMinuteAgo
        );
        
        if (recentDuplicate) {
          return prev;
        }
      }
      
      return [newNotification, ...prev].slice(0, 50); // Keep only last 50 notifications
    });
  };

  const markAsRead = (notificationId) => {
    setNotifications(prev => 
      prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
    );
  };

  const markAllAsRead = () => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  };

  const removeNotification = (notificationId) => {
    setNotifications(prev => prev.filter(n => n.id !== notificationId));
  };

  const clearAllNotifications = () => {
    setNotifications([]);
  };
  
  const removeIncomingAttackNotification = (attackerUsername) => {
    setNotifications(prev => prev.filter(n => 
      !(n.type === 'incoming_attack' && n.attackerUsername === attackerUsername)
    ));
  };

  // Helper functions for specific notification types
  const notifyPartPurchase = (partName, cost, category) => {
    const icons = {
      mining: '⛏️',
      bruteforce: '💻',
      network: '🌊',
      security: '🛡️'
    };

    addNotification({
      type: 'purchase',
      category: 'shop',
      title: 'Part Purchased',
      message: `Successfully bought ${partName}`,
      details: `Cost: ${cost} WR Coins`,
      icon: icons[category] || '🛒',
      color: '#4CAF50'
    });
  };

  const notifyComputerMove = (cost, moveCount, isLocked) => {
    addNotification({
      type: 'movement',
      category: 'computer',
      title: 'Computer Moved',
      message: `Computer relocated (Move ${moveCount}/3)`,
      details: `Cost: ${cost} WR Coins${isLocked ? ' - Now LOCKED for 24h' : ''}`,
      icon: '🖥️',
      color: isLocked ? '#FF9800' : '#2196F3'
    });
  };

  const notifyAttackStarted = (targetUsername, duration, toolsUsed) => {
    addNotification({
      type: 'attack_started',
      category: 'attack',
      title: 'Network Attack Initiated',
      message: `Attacking ${targetUsername}`,
      details: `Duration: ${duration}h${toolsUsed > 0 ? ` (${toolsUsed} tools used)` : ''}`,
      icon: '🚀',
      color: '#FF5722'
    });
  };

  const notifyAttackCompleted = (targetUsername, stolenAmount, xpGained, success) => {
    addNotification({
      type: 'attack_completed',
      category: 'attack',
      title: success ? 'Attack Successful!' : 'Attack Failed',
      message: success ? `Hacked ${targetUsername} successfully` : `Attack on ${targetUsername} failed`,
      details: success ? `Stolen: ${stolenAmount} WR Coins, +${xpGained} XP` : 'Target had insufficient funds',
      icon: success ? '💰' : '❌',
      color: success ? '#4CAF50' : '#F44336'
    });
  };

  const notifyIncomingAttack = (attackerUsername, defenseLevel) => {
    addNotification({
      type: 'incoming_attack',
      category: 'defense',
      title: 'Under Attack!',
      message: `${attackerUsername} is attacking your computer`,
      details: `Defense Level: ${defenseLevel} firewall parts`,
      icon: '⚠️',
      color: '#FF9800',
      attackerUsername: attackerUsername // Add this for easier deduplication
    });
  };

  const notifyDefenseSuccess = (attackerUsername, blockedAmount) => {
    addNotification({
      type: 'defense_success',
      category: 'defense',
      title: 'Attack Defended',
      message: `Successfully defended against ${attackerUsername}`,
      details: `Prevented loss of ${blockedAmount} WR Coins`,
      icon: '🛡️',
      color: '#4CAF50'
    });
  };

  const notifyBruteForceStarted = (targetType, targetId, duration) => {
    addNotification({
      type: 'brute_force_started',
      category: 'hacking',
      title: 'Brute Force Started',
      message: `Attacking ${targetType} ${targetId}`,
      details: `Estimated duration: ${duration} days`,
      icon: targetType === 'chest' ? '📦' : '🏦',
      color: '#9C27B0'
    });
  };

  const notifyBruteForceCompleted = (targetType, targetId, success, code) => {
    addNotification({
      type: 'brute_force_completed',
      category: 'hacking',
      title: success ? 'Code Cracked!' : 'Brute Force Failed',
      message: success ? `Successfully cracked ${targetType} ${targetId}` : `Failed to crack ${targetType} ${targetId}`,
      details: success ? `Access Code: ${code}` : 'Security system blocked the attack',
      icon: success ? '🔓' : '🔒',
      color: success ? '#4CAF50' : '#F44336'
    });
  };

  const notifyMiningReward = (amount, efficiency) => {
    addNotification({
      type: 'mining_reward',
      category: 'mining',
      title: 'Mining Rewards Collected',
      message: `Collected ${amount.toFixed(3)} WR Coins`,
      details: `Mining efficiency: ${(efficiency * 100).toFixed(0)}%`,
      icon: '⛏️',
      color: '#FFC107'
    });
  };

  const value = {
    notifications,
    unreadCount,
    addNotification,
    markAsRead,
    markAllAsRead,
    removeNotification,
    clearAllNotifications,
    removeIncomingAttackNotification,
    // Helper functions
    notifyPartPurchase,
    notifyComputerMove,
    notifyAttackStarted,
    notifyAttackCompleted,
    notifyIncomingAttack,
    notifyDefenseSuccess,
    notifyBruteForceStarted,
    notifyBruteForceCompleted,
    notifyMiningReward
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};